import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { PageHeader } from "@/components/shared";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";
import { TrendingUp, AlertTriangle, AlertOctagon, Info, CheckCircle2 } from "lucide-react";
import { computeRange } from "@/lib/dateRange";
import { useFilter } from "@/context/FilterContext";

const RAG_BADGE = { green: "rag-green", amber: "rag-amber", red: "rag-red" };
const INSIGHT_ICON = { positive: CheckCircle2, warning: AlertTriangle, danger: AlertOctagon, neutral: Info };
const INSIGHT_COLOR = { positive: "text-[#059669]", warning: "text-[#F59E0B]", danger: "text-[#DC2626]", neutral: "text-slatetext" };

const CHART_TOOLTIP = { backgroundColor: "#1E293B", border: "1px solid #334155", borderRadius: 8, color: "#F8FAFC" };
const SERIES = ["#C9A24B", "#059669", "#38BDF8", "#A78BFA", "#F472B6"];

export default function Dashboard() {
  const { range, role } = useFilter();
  const computed = computeRange(range);

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", role, computed.start, computed.end],
    queryFn: async () => {
      const params = { ...computed };
      if (role) params.role = role;
      return (await api.get("/dashboard", { params })).data;
    },
  });
  const { data: insightsData } = useQuery({
    queryKey: ["insights"],
    queryFn: async () => (await api.get("/insights")).data,
  });

  const blocks = data?.blocks || [];
  const charts = data?.charts || {};
  const followerKeys = charts.follower_trend?.length
    ? Object.keys(charts.follower_trend[0]).filter((k) => k !== "date")
    : [];

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Auto-generated KPI scorecard — filtered by the global Role / Employee / Date bar above" />

      {/* Insights */}
      <div className="mb-6 rounded-lg border border-gold/30 bg-surface p-5" data-testid="insights-feed">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-5 h-5 text-gold" />
          <h3 className="font-heading font-bold">Auto Insights</h3>
        </div>
        <div className="grid md:grid-cols-2 gap-2">
          {(insightsData?.insights || []).map((ins, i) => {
            const Icon = INSIGHT_ICON[ins.type] || Info;
            return (
              <div key={i} className="flex items-start gap-2.5 text-sm py-1.5">
                <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${INSIGHT_COLOR[ins.type]}`} />
                <span className="text-slate-200">{ins.text}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* KPI Scorecards */}
      {isLoading ? (
        <div className="text-slatetext py-10 text-center">Loading dashboard…</div>
      ) : (
        <div className="grid gap-6 mb-6" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))" }}>
          {blocks.map((block) => (
            <div key={block.role} className="rounded-lg border border-surface-hover bg-surface p-6" data-testid={`kpi-block-${block.role.replace(/\s/g, "-")}`}>
              <h3 className="font-heading font-bold mb-4 text-gold">{block.role}</h3>
              <div className="space-y-3">
                {block.metrics.map((m) => (
                  <div key={m.key} className="flex items-center justify-between">
                    <span className="text-sm text-slatetext">{m.label}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-heading font-bold tabular-nums">{Number(m.actual).toLocaleString("en-IN")}</span>
                      {m.rag === "none" ? (
                        <span className="text-xs text-slatetext">no target</span>
                      ) : (
                        <span className="text-xs text-slatetext">/ {Number(m.target).toLocaleString("en-IN")}</span>
                      )}
                      <span className={`w-2.5 h-2.5 rounded-full ${m.rag === "green" ? "bg-[#059669]" : m.rag === "amber" ? "bg-[#F59E0B]" : m.rag === "red" ? "bg-[#DC2626]" : "bg-slate-600"}`} title={m.rag} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        <ChartCard title="Cross-role Output Trend">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={charts.output_trend || []}>
              <CartesianGrid stroke="#334155" strokeDasharray="3 3" />
              <XAxis dataKey="week" stroke="#94A3B8" fontSize={11} />
              <YAxis stroke="#94A3B8" fontSize={11} />
              <Tooltip contentStyle={CHART_TOOLTIP} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {["PR", "Social", "Video", "Writing", "Design"].map((k, i) => (
                <Line key={k} type="monotone" dataKey={k} stroke={SERIES[i]} strokeWidth={2} dot={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Follower Growth Trend">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={charts.follower_trend || []}>
              <CartesianGrid stroke="#334155" strokeDasharray="3 3" />
              <XAxis dataKey="date" stroke="#94A3B8" fontSize={11} />
              <YAxis stroke="#94A3B8" fontSize={11} />
              <Tooltip contentStyle={CHART_TOOLTIP} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {followerKeys.map((k, i) => (
                <Line key={k} type="monotone" dataKey={k} stroke={SERIES[i % SERIES.length]} strokeWidth={2} dot={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Influencer ROI Comparison">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={charts.roi_compare || []}>
              <CartesianGrid stroke="#334155" strokeDasharray="3 3" />
              <XAxis dataKey="name" stroke="#94A3B8" fontSize={10} />
              <YAxis stroke="#94A3B8" fontSize={11} />
              <Tooltip contentStyle={CHART_TOOLTIP} />
              <Bar dataKey="roi" fill="#C9A24B" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Content Pipeline Throughput">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={charts.throughput || []}>
              <CartesianGrid stroke="#334155" strokeDasharray="3 3" />
              <XAxis dataKey="stage" stroke="#94A3B8" fontSize={9} interval={0} angle={-20} textAnchor="end" height={60} />
              <YAxis stroke="#94A3B8" fontSize={11} />
              <Tooltip contentStyle={CHART_TOOLTIP} />
              <Bar dataKey="count" fill="#059669" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="rounded-lg border border-surface-hover bg-surface p-6">
      <h3 className="font-heading font-bold mb-4">{title}</h3>
      {children}
    </div>
  );
}
